import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  LayoutDashboard,
  Scissors,
  Image,
  Star,
  CalendarDays,
  MessageSquare,
  LogOut,
  Plus,
  Trash2,
  Pencil,
  X,
  Save,
  Upload,
  Loader2,
  Bell,
  CheckCircle,
  Sparkles,
  Megaphone,
  ExternalLink,
  Copy,
  Eye,
  EyeOff,
  Send,
  Phone,
  Users,
  Mail,
  CalendarPlus,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";
import { API, SITE_URL, authHeaders, resolveUrl, ImageUploadField } from "./shared";

const StyleRequestsTab = () => {
  const [requests, setRequests] = useState([]);

  const fetchRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/custom-style-requests`, authHeaders());
      setRequests(res.data);
    } catch (err) {
      // error handled silently
    }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/custom-style-requests/${id}/status?status=${status}`, {}, authHeaders());
      toast.success(`Request marked as ${status}`);
      fetchRequests();
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  return (
    <div data-testid="admin-requests-tab">
      <h2 className="font-serif text-2xl text-brand-text mb-6">Custom Style Requests</h2>
      {requests.length === 0 ? (
        <p className="font-sans text-sm text-brand-text-muted">No custom style requests yet.</p>
      ) : (
        <div className="space-y-3">
          {requests.map((req) => (
            <div key={req.id} className="p-4 border border-brand-border bg-brand-surface" data-testid={`admin-request-${req.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-sans text-sm font-medium text-brand-text">{req.name}</p>
                  <p className="font-sans text-xs text-brand-gold">{req.email} | {req.phone}</p>
                </div>
                <span className={`font-sans text-[10px] px-2 py-0.5 border ${
                  req.status === "pending" ? "bg-yellow-900/30 text-yellow-400 border-yellow-800" :
                  req.status === "quoted" ? "bg-blue-900/30 text-blue-400 border-blue-800" :
                  req.status === "accepted" ? "bg-green-900/30 text-green-400 border-green-800" :
                  "bg-brand-surface border-brand-border text-brand-text-muted"
                }`}>{req.status}</span>
              </div>
              <div className="mt-2 p-3 bg-brand-bg border border-brand-border">
                <p className="font-sans text-xs text-brand-text-muted mb-1 uppercase tracking-wider">Style Requested:</p>
                <p className="font-sans text-sm text-brand-text">{req.style_description}</p>
              </div>
              {req.proposed_budget && (
                <p className="font-sans text-xs text-brand-text-muted mt-2">
                  Client's budget: <span className="text-brand-gold font-semibold">{req.proposed_budget}</span>
                </p>
              )}
              <p className="font-sans text-[10px] text-brand-text-muted/60 mt-1">{new Date(req.created_at).toLocaleString()}</p>
              {req.status === "pending" && (
                <div className="flex gap-2 mt-3">
                  <button onClick={() => updateStatus(req.id, "quoted")} data-testid={`quote-request-${req.id}`} className="px-3 py-1.5 bg-brand-gold text-black font-sans text-xs font-semibold hover:bg-brand-gold-light transition-colors">
                    Mark as Quoted
                  </button>
                  <button onClick={() => updateStatus(req.id, "declined")} className="px-3 py-1.5 border border-brand-border text-brand-text-muted font-sans text-xs hover:text-red-400 hover:border-red-400 transition-colors">
                    Decline
                  </button>
                </div>
              )}
              {req.status === "quoted" && (
                <div className="flex gap-2 mt-3">
                  <button onClick={() => updateStatus(req.id, "accepted")} className="px-3 py-1.5 bg-green-800 text-green-200 font-sans text-xs font-semibold hover:bg-green-700 transition-colors">
                    Client Accepted
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StyleRequestsTab;
